'use client';

import { useEffect, useRef } from 'react';
import { IconPaperclip, IconPlus, IconSend } from '@tabler/icons-react';
import clsx from 'clsx';
import { ActionIcon, FileButton, Group, Textarea } from '@mantine/core';
import { DAttachmentUploadMeta } from '@/src/modules/chat/data/dtos/attachment-upload-meta';
import { useChatComposerContext } from './contexts';

export interface IChatMessageComposerProps {
  selectedConversationId?: number | null;
  size?: 'sm' | 'md';
  supportedFileUploads: TSupportedFileUpload[];
  isSendingMessage?: boolean;
  onSendMessage: (content: string | null, attachments: File[]) => Promise<void>;
  captureMode?: 'environment' | 'user';
  isDisabled?: boolean;
  preserveContentOnConversationChange?: boolean;
}

export default function ChatMessageComposer({
  size = 'md',
  selectedConversationId,
  onSendMessage,
  isSendingMessage,
  captureMode,
  supportedFileUploads,
  isDisabled,
  preserveContentOnConversationChange = false,
}: IChatMessageComposerProps) {
  if (!supportedFileUploads.includes('image') && captureMode) {
    throw new Error(
      'captureMode is not supported when image upload is not enabled'
    );
  }

  const isNormalSize = size === 'md';
  const actionIconSize = isNormalSize ? '2.5rem' : '2.3rem';
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const {
    content,
    setContent,
    addAttachments,
    attachments,
    resetState: resetContext,
    resetFileUploadRef,
  } = useChatComposerContext();
  const attachmentExists = attachments.length > 0;
  const canSendMessage = content.trim() !== '' || attachmentExists;

  const onInputChanged = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContent(event.target.value);
  };

  const onUploadAttachments = (files: File[]) => {
    if (files.length === 0) {
      return;
    }
    const fileUploadMeta: DAttachmentUploadMeta[] = files.map((file) => ({
      id: crypto.randomUUID(),
      previewUrl: URL.createObjectURL(file),
      file,
    }));
    addAttachments(fileUploadMeta);
  };

  const handleOnSendMessage = async () => {
    const files = attachments.map((attachment) => attachment.file);
    await onSendMessage(content, files);
    resetContext();

    const focusToTextArea = setTimeout(() => textAreaRef.current?.focus(), 100);
    return () => clearTimeout(focusToTextArea);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const notPressedOtherKeyThanEnter =
      !event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey;
    if (notPressedOtherKeyThanEnter && event.key === 'Enter') {
      event.preventDefault();
      handleOnSendMessage();
    }
  };

  const createAcceptType = (input: TSupportedFileUpload) => {
    switch (input) {
      case 'image':
        return 'image/*';
      case 'video':
        return 'video/*';
      default:
        return '*/*';
    }
  };

  const acceptTypes = supportedFileUploads.map(createAcceptType).join(',');

  useEffect(() => {
    if (!textAreaRef.current) {
      return;
    }
    textAreaRef.current!.focus();

    const valueLength = textAreaRef.current.value.length;
    textAreaRef.current!.setSelectionRange(valueLength, valueLength);
  }, [textAreaRef]);

  useEffect(() => {
    if (preserveContentOnConversationChange) {
      return;
    }

    resetContext();
  }, [selectedConversationId, preserveContentOnConversationChange]);

  return (
    <Group justify="center" gap={0}>
      <Textarea
        ref={textAreaRef}
        value={content}
        flex={1}
        placeholder="Tekan Enter untuk mengirim"
        onChange={onInputChanged}
        onKeyDown={onKeyDown}
        disabled={isSendingMessage}
        variant="unstyled"
        autosize
        maxRows={5}
        classNames={{
          input: clsx('px-sm!', !isNormalSize && 'placeholder:text-[0.8rem]!'),
        }}
        mr={0}
      />
      <Group gap="sm">
        <FileButton
          onChange={onUploadAttachments}
          resetRef={resetFileUploadRef}
          accept={acceptTypes}
          multiple
          disabled={isSendingMessage || isDisabled}
          capture={captureMode}
        >
          {(props) => (
            <ActionIcon
              size={actionIconSize}
              color="gray"
              variant="transparent"
              {...props}
            >
              {attachmentExists ? (
                <IconPlus size={20} />
              ) : (
                <IconPaperclip size={20} />
              )}
            </ActionIcon>
          )}
        </FileButton>

        <ActionIcon
          size={actionIconSize}
          onClick={handleOnSendMessage}
          loading={isSendingMessage}
          disabled={!canSendMessage || isDisabled}
        >
          <IconSend size={16} />
        </ActionIcon>
      </Group>
    </Group>
  );
}
